import {
  Component,
  ElementRef,
  HostListener,
  Injectable,
  Input,
  OnChanges,
  ViewChild,
  OnDestroy,
  PLATFORM_ID,
  Inject,
  signal,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, takeUntil, startWith, shareReplay } from 'rxjs/operators';
import { MenuItemModel, UserModel } from '../../models';
import { AuthService } from '../../services/auth.service';
import { PromotionService } from '../../services/promotion.service';
import { UserState, UserStateModel } from '../../store/user/state/user.state';
import { NgxObserveDirective } from 'ngx-observe';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { FocusService } from '../../services/focus.service';
import { AppButton } from '../../uikit/button/button.component';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, NgxObserveDirective, AppButton],
})
@Injectable()
export class NavigationComponent implements OnChanges, OnDestroy {
  public value: 'Select';
  public isOpen = false;
  public mobileIsOpen = false;
  public isScrolled = false;
  public freidaMenuName = 'main';
  public user: UserModel;
  public tooltipText = 'Join the AMA to get access to all the tools you need.';
  public menus: MenuItemModel[] = [];
  public menuLinks = {};
  public newNavArray: any = [];
  public newChildren: any = [];
  public newIndex: number;
  public level = 1;
  public showToolsResourcesMenu = false;
  public disableUpdates$ = signal(false);

  private destroy$ = new Subject<void>();

  @Input() private menuItems: MenuItemModel[];

  @Select(UserState.isMember)
  public isMember$: Observable<boolean>;
  public isAuthenticated$: Observable<UserStateModel>;

  @Select(UserState)
  public user$: Observable<UserStateModel>;
  public currentUser$;

  private focusSubscription!: Subscription;
  @ViewChild('programDirectorPortalLink') programDirectorPortalLink!: ElementRef;
  @ViewChild('navigationDashboardButton') navigationDashboardButton!: ElementRef;

  constructor(
    private router: Router,
    private authService: AuthService,
    protected promotionService: PromotionService,
    private focusService: FocusService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    if (typeof platformId === 'string' || isPlatformBrowser(platformId)) {
      effect(() => {
        this.disableUpdates$.set(this.promotionService.disableUpdates());
      });
    }

    this.currentUser$ = this.user$.pipe(
      startWith({ user: new UserModel() } as UserStateModel), // Provide immediate default
      map((userState) => {
        const user = new UserModel(userState.user);
        this.user = user;
        return user;
      }),
      shareReplay(1),
    );

    this.focusSubscription = this.focusService.focusRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe((elementId: string) => {
        if (this.programDirectorPortalLink && elementId === 'programDirectorPortalLink') {
          this.programDirectorPortalLink.nativeElement.focus();
        }
        if (this.navigationDashboardButton && elementId === 'navigationDashboardButton') {
          this.navigationDashboardButton.nativeElement.focus();
        }
      });
  }

  public toggleToolsResourcesMenu() {
    this.showToolsResourcesMenu = !this.showToolsResourcesMenu;
  }

  private _clickedInsideNav = false;

  public onNavClick(): void {
    this._clickedInsideNav = true;
  }

  @HostListener('document:click')
  public onDocumentClick(): void {
    if (this.showToolsResourcesMenu && !this._clickedInsideNav) {
      this.toggleToolsResourcesMenu();
    }
    this._clickedInsideNav = false;
  }

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    this.isScrolled = window.scrollY > 0;
    if (this.showToolsResourcesMenu) {
      this.toggleToolsResourcesMenu();
    }
  }

  public redirectToFaq() {
    this.router.navigate(['contact']);
  }

  public ngOnChanges() {
    if (this.menuItems) {
      const subMenus = this.menuItems
        .filter((m) => m.children.length > 0)
        .map((menu) => ({
          [menu.title]: menu.children.map((link) => ({ value: link.url, text: link.title })),
        }));
      for (const subMenu of subMenus) {
        this.menuLinks = { ...this.menuLinks, ...subMenu };
      }
      this.menus = this.menuItems;
    }
  }

  public resetNav(): void {
    this.isOpen = false;
    this.showToolsResourcesMenu = false;
  }

  public navigateTo(url: string, event) {
    event.preventDefault();
    this.showToolsResourcesMenu = false;
    this.isOpen = false;
    const directorUrl = url.includes('/director');
    let nextUrl = url;

    if (this.user && !this.user.isAuthenticated && !this.user.isProgramMaintainer && directorUrl) {
      nextUrl = this.authService.getSsoSignInUrl(nextUrl);
    }

    if (nextUrl.startsWith('http')) {
      location.href = nextUrl;
      this.resetNav();
    } else {
      this.router.navigateByUrl(nextUrl);
      this.resetNav();
    }
  }

  public roadToResidencyLink(event) {
    event.preventDefault();

    this.showToolsResourcesMenu = false;
    if (this.user && this.user.isMember) {
      this.router.navigate(['/road-to-residency-guide/overview']);
    } else {
      this.router.navigate(['/road-to-residency-guide']);
    }
  }

  public goToDashboard() {
    this.resetNav();
    this.router.navigate(['/dashboard-landing']);
  }

  public goToProgramDirectorPortal(user, event) {
    event.preventDefault(); // prevent default href navigation/behavior
    if (user?.roles.includes('authenticated')) {
      this.resetNav();
      this.router.navigate(['/director']);
    } else if (isPlatformBrowser(this.platformId)) {
      // Get the full SSO URL
      const ssoUrl = this.authService.getSsoSignInUrl(window.location.hostname + '/director');

      // Use window.location.href for external navigation
      window.location.href = ssoUrl;
    }
  }

  public focusOnElementFFException() {
    //Untrap focus and force focus on next component for Firefox browsers
    this.focusService.requestFocus('heroComponent');
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
